/**
 *
 * OCRTextPage
 *
 */

import * as React from 'react';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useSize } from '@domonda/ui/hooks';
import { OCRPage } from 'lib/api/ocrResult';
import { ToastContainer } from 'react-toastify';

// parts
import { OCRTextPageRegion } from './OCRTextPageRegion';
import {
  OCRTextPageDetectionHighlighter,
  OCRTextPageDetection,
  OCRTextPageDetections,
} from './OCRTextPageDetectionHighlighter';
export { OCRTextPageDetection, OCRTextPageDetections };

// decorate
import { decorate, Decorate } from './decorate';
import { FlatInvoiceDetail } from 'lib/api/flatInvoiceDetails';
import { getConfigFromStore } from 'lib/api/customerConfig';

interface OCRTextPage extends React.HTMLAttributes<HTMLDivElement> {
  page: OCRPage;
  thresholds: {};
  onScaleChange: (s: number) => void;
  detections: OCRTextPageDetection[];
  highlightedDetection: OCRTextPageDetection | null;
  onDetectionClick?: (detection: OCRTextPageDetection) => void;
  outlinedPage?: boolean;
  outlinedRegions?: boolean;
  outlinedLines?: boolean;
  visibleWords?: boolean;
  outlinedWords?: boolean;
  disableWordsText?: boolean;
}

const OCRTextPage: React.FC<OCRTextPage & Decorate> = (props) => {
  const {
    children,
    onScaleChange,
    thresholds,
    classes,
    className,
    page,
    detections,
    highlightedDetection,
    onDetectionClick,
    outlinedPage,
    outlinedRegions,
    outlinedLines,
    visibleWords,
    outlinedWords,
    disableWordsText,
    ...rest
  } = props;
  const { Width: pageWidth, Height: pageHeight, Regions } = page;

  // derive the scale factor using the container size
  const [containerRef, containerSize] = useSize<HTMLDivElement>();
  const scale = useMemo<number>(() => {
    const { height: containerHeight } = containerSize;
    if (containerHeight == null) {
      return 0;
    }

    onScaleChange(containerHeight / pageHeight);
    return containerHeight / pageHeight;
  }, [containerSize, pageHeight]);

  const shouldBeHighlighted = (detection: FlatInvoiceDetail) => {

    // Hide highlighted items that are not on detections fields form for demo purposes
    if (
      detection.Id.includes('DiscountGroup') ||
      detection.Id.includes('BankGroup') ||
      detection.Id.includes('DueDateGroup') ||
      detection.Id.includes('Receiver') ||
      detection.Id.includes('CustomerId') ||
      detection.Id.includes('Bic') ||
      detection.Id.includes('TaxNumber') ||
      detection.Id.includes('Sender-Email') ||
      detection.Id.includes('Sender-Name')
    ) {
      return false;
    }

    let configFieldKey = '';
    const idSplit = detection.Id.split('-');
    if (idSplit[0] === 'Sender' || idSplit[0] === 'Receiver') {
      if (
        detection.Type === 'Country' ||
        detection.Type === 'City' ||
        detection.Type === 'ZipCode' ||
        detection.Type === 'Street'
      )
        configFieldKey = `${idSplit[0]}.Address.${detection.Type}`;
      else configFieldKey = `${idSplit[0]}.${detection.Type}`;
    } else if (idSplit[0] === 'VatGroup') {
      configFieldKey = `${idSplit[0]}.${detection.Type}`;
    } else configFieldKey = detection.Type;
/*
    const config = getConfigFromStore();
    const configValue = config.InvoiceDetails.find((el) => el.Name === configFieldKey);
    if (!configValue) return false;
    else return configValue.Priority === 'MustHave' || configValue.Priority === 'NiceToHave';
*/
return true;
  };

  // memoize page regions to avoid unnecessary re-renders
  const pageRegions = useMemo(
    () =>
      scale > 0 &&
      Regions.map((region, index) => (
        <OCRTextPageRegion
          key={`region-${index.toString()}`}
          scale={scale}
          region={region}
          outlined={Boolean(outlinedRegions)}
          outlinedLines={Boolean(outlinedLines)}
          visibleWords={Boolean(visibleWords)}
          outlinedWords={Boolean(outlinedWords)}
          disableWordsText={Boolean(disableWordsText)}
        />
      )),
    [
      scale,
      Regions,
      Boolean(outlinedRegions),
      Boolean(outlinedLines),
      Boolean(visibleWords),
      Boolean(outlinedWords),
      Boolean(disableWordsText),
    ],
  );

  return (
    <div {...rest} ref={containerRef} className={clsx(classes.root, className)}>
      {scale > 0 && (
        <div
          className={clsx(classes.page, outlinedPage && classes.outlined)}
          style={{
            width: pageWidth * scale,
            height: pageHeight * scale,
          }}
        >
          {detections.map(
            (detection, index) =>
              shouldBeHighlighted(detection) && (
                <OCRTextPageDetectionHighlighter
                  thresholds={thresholds}
                  key={`detection-${index.toString()}`}
                  scale={scale}
                  detection={detection}
                  highlighted={
                    highlightedDetection
                      ? detection.X > 0 &&
                        detection.Y > 0 &&
                        highlightedDetection.Id === detection.Id
                      : false
                  }
                  onDetectionClick={onDetectionClick}
                />
              ),
          )}
          {pageRegions}
        </div>
      )}
      <ToastContainer />
    </div>
  );
};

const ComposedOCRTextPage = React.memo(decorate(OCRTextPage));
export { ComposedOCRTextPage as OCRTextPage };
